// 감사 기록(AuditLog)의 **표기 정본 한 벌**.
//
// 감사 UI 는 windows 뿐이라 shared 로 올리지 않는다(정본이 두 벌 되는 것이 더 위험).
// 이 파일이 책임지는 것 네 가지:
//   ① 액션 이름   — 서버 `@Audit({action})` 전수를 한국어로 (ACTION_PARTS)
//   ② 문장        — "홍길동님이 …을 …" 한 줄 (formatAuditLogSentence)
//   ③ diff 필드명 — 변경 전/후 표의 왼쪽 칸 (formatDiffFieldLabel)
//   ④ diff 값     — 변경 전/후 표의 값 칸 (formatDiffValue)
//
// 개선 9건 차수2(26-08-04) §3 — 종전엔 ①만 있었고 그것도 절반이 자동 조합 표기에
// 기대 영문이 그대로 노출됐다(예: 'loaderRev.unapply' → 'loaderRev unapply').
// 서버에 없는 stale 키 10개도 섞여 있었다. 이번에 다음을 세운다:
//   • 서버 액션 전수 수록 + **드리프트 시험**(audit-log-format.test.ts) —
//     서버에 새 @Audit 이 생기면 시험이 먼저 깨져 "문구를 정하라"고 알려 준다.
//   • ②·④ 를 이 파일로 모음 — ④ 는 종전에 AuditLogsPage 안의 지역 함수였고,
//     문장 렌더러가 같은 판정을 다시 구현하면 표와 문장이 다른 말을 하게 된다.
//
// ⚠ 감사 장부는 append-only(위·변조 방지 해시체인)라 **과거 기록은 고쳐지지 않는다.**
//   그래서 (a) 서버에서 사라진 액션이라도 과거 행에 남아 있으면 라벨이 필요하고
//   (LEGACY_ACTION_PARTS), (b) 문장은 저장된 것 이상을 말하지 않는다.
//
// 개선 9건 차수2c(26-08-04) 결함③ — (b) 의 꼬리말이 **거짓을 말하고 있었다.**
//   종전 문장은 diff 가 없고 동작이 수정 계열이면 "(바뀐 내용은 기록되지 않던
//   시기입니다)" 를 붙였다. 그 판정은 '시기'가 아니라 **'그 경로가 diff 를 싣는가'**
//   를 봐야 하는데 시기로 말했다. 차수2b 가 저장을 확대한 것은 네 경로뿐이고, 같은
//   mutation 동사를 쓰는 나머지 경로(로더·딜·리포트·개조 …)는 **지금도** diff 를
//   싣지 않는다 → 오늘 만든 기록에도 "기록되지 않던 시기"라고 적혔다.
//   이제 꼬리말을 **경로의 성질**로 가른다(DIFF_CARRYING_ACTIONS + 두 꼬리말).

import {
  DEPLOYMENT_KIND_LABELS as SHARED_DEPLOYMENT_KIND_LABELS,
  DEPLOYMENT_RETURN_REASON_LABELS,
  ROBOT_RETURN_LOCATION_LABELS,
  ROBOT_STATUS_LABELS,
  ROBOT_VERIFICATION_LABELS,
  SERVICE_ORDER_STATUS_LABELS,
} from '@bhops/shared';
import { CUSTOMER_MASTER_STATUS_LABEL } from '@/api/customer-master';
import { CONTRACT_REASON_LABEL, formatContractTypeSummary } from './contract-reason';
import { kstYmd } from './format-date';

// ============================================================================
// ① 액션 이름 — 서버 @Audit action 전수
// ============================================================================
//
// 한 항목은 **[목적어, 서술어]** 다. 표의 '액션' 칸은 두 조각을 이어 붙이고
// (`목적어 + ' ' + 서술어`), 문장은 조각을 조사와 함께 엮는다. 라벨 문자열을 통째로
// 적어 두면 문장 쪽이 그것을 다시 쪼개야 해서(어디가 동사인지 추측) 반드시 어긋난다.
//
// 목적어가 null 인 것은 목적어 없는 사건(로그인 등)이다.
export type AuditActionParts = readonly [object: string | null, verb: string];

export const ACTION_PARTS: Record<string, AuditActionParts> = {
  // ── 인증 ─────────────────────────────────────────────────────────
  'auth.login': [null, '로그인'],
  'auth.logout': [null, '로그아웃'],
  'auth.refresh': ['토큰', '갱신'],
  'auth.google': [null, '구글 로그인'],
  // 모바일 APK 내장 기기 키로 발급받는 서비스 계정 토큰(google-sso-auth-2026-06).
  'auth.device': ['기기', '로그인'],
  'auth.password.changed': ['비밀번호', '변경'],
  'auth.2fa.setup': ['2단계 인증', '설정'],
  'auth.2fa.verify': ['2단계 인증', '확인'],
  'auth.2fa.verified': ['2단계 인증', '통과'],
  'auth.2fa.disable': ['2단계 인증', '해제'],

  // ── 사용자 ───────────────────────────────────────────────────────
  'user.create': ['사용자', '등록'],
  'user.update': ['사용자', '수정'],
  'user.delete': ['사용자', '삭제'],
  'user.reset_password': ['사용자 비밀번호', '초기화'],

  // ── 로봇 ─────────────────────────────────────────────────────────
  'robot.create': ['로봇', '등록'],
  // §3 예시 문장의 주인공 — "…로봇 정보를 수정(바뀐 내용은 기록되지 않던 시기입니다)".
  'robot.update': ['로봇 정보', '수정'],
  'robot.delete': ['로봇', '삭제'],
  'robot.restore': ['로봇', '복구'],
  'robot.permanent-delete': ['로봇', '완전삭제'],
  'robot.transfer': ['로봇', '이관'],
  'robot.exported': ['로봇 목록', '내보내기'],
  // 로봇 기준일 마감본(2026-10-08) — 말일 자동 · 관리자 지금 마감 · 백업 복원 채우기 모두 이 한 줄.
  'robot-snapshot.capture': ['로봇 기준일 마감본', '생성'],
  'robot.status-correction': ['로봇 상태', '정정'],
  // 구분 수동 정정·사후 계약 연결(2026-09-01, plan retire-rollback-sale-kind-2026-09)
  'robot.contract-kind-correction': ['로봇 구분', '정정'],
  'robot.contract-link': ['로봇 계약 연결', '등록'],
  'robot.contract-unlink': ['로봇 계약 연결', '해제'],
  // 로봇 상세 '최근 이력' 편집(2026-11-02) — 시점 되돌리기 · 줄 지우기 · 다시 살리기.
  'robot.history-rewind': ['로봇 이력', '되돌리기'],
  'robot.history-hide': ['로봇 이력 줄', '숨김'],
  'robot.history-revert': ['로봇 이력 편집', '취소'],
  'robot.qr.issue': ['QR', '발급'],
  'robot.qr.revoke': ['QR', '폐기'],
  'robot.ecn.apply': ['로봇 변경점', '적용'],
  'robot.ecn.remove': ['로봇 변경점', '해제'],

  // ── 인수(인수증) ─────────────────────────────────────────────────
  'robot-acceptance.finalize': ['인수', '확정'],
  'acceptance.delete': ['인수증', '삭제'],
  'acceptance.pdf.view': ['인수증 PDF', '열람'],

  // ── 매장 ─────────────────────────────────────────────────────────
  'store.create': ['매장', '등록'],
  'store.update': ['매장', '수정'],
  'store.delete': ['매장', '삭제'],
  'store.restore': ['매장', '복구'],

  // ── 고객사 ───────────────────────────────────────────────────────
  'customer.create': ['고객사', '등록'],
  'customer.update': ['고객사', '수정'],
  'customer.delete': ['고객사', '삭제'],
  'customer.restore': ['고객사', '복구'],

  // ── 고객사 마스터(통합 시트) ─────────────────────────────────────
  'customer-master.create': ['고객사 마스터', '등록'],
  'customer-master.update': ['고객사 마스터', '수정'],
  'customer-master.status.update': ['고객사 진행 상태', '변경'],
  'customer-master.request-robot-assignment': ['로봇 할당', '요청'],

  // ── 계약 갱신 검수(차수2d) ───────────────────────────────────────
  // '확인 필요'로 올라온 갱신 기록을 사람이 "갱신 맞음 / 날짜 정정"으로 확정하는 자리.
  'contract-renewal.review': ['갱신 기록 검수', '확정'],

  // ── 고객사 메모 ──────────────────────────────────────────────────
  'customer-note.create': ['고객사 메모', '등록'],
  'customer-note.update': ['고객사 메모', '수정'],
  'customer-note.delete': ['고객사 메모', '삭제'],

  // ── 활성 고객사 화면 ─────────────────────────────────────────────
  'active-customers.maintenance.create': ['정비 작업 지시', '등록'],
  'active-customers.return-date.set': ['반납 예정일', '설정'],
  'active-customers.return.complete': ['로봇 회수', '완료'],

  // ── 계약 ─────────────────────────────────────────────────────────
  'contract.create': ['계약', '등록'],
  'contract.update': ['계약', '수정'],
  'contract.delete': ['계약', '삭제'],
  'contract.sign': ['계약', '서명'],
  'contract.terminate': ['계약', '해지'],
  'contract.reschedule_install': ['설치 일정', '변경'],

  // ── 딜 ───────────────────────────────────────────────────────────
  'deal.create': ['딜', '등록'],
  'deal.update': ['딜', '수정'],
  'deal.delete': ['딜', '삭제'],
  'deal.restore': ['딜', '복구'],
  'deal.hard-delete': ['딜', '완전삭제'],
  'deal.transition': ['딜', '상태 변경'],
  'deal.activity.create': ['딜 활동', '추가'],

  // ── 배정 / 배치 ──────────────────────────────────────────────────
  'assignment.release': ['로봇 배정', '해제'],
  'deployment.create': ['배치', '등록'],
  'deployment.return': ['로봇', '반납'],

  // ── 서비스오더 ───────────────────────────────────────────────────
  'serviceorder.closed': ['서비스오더', '종료'],
  'serviceorder.delete': ['서비스오더', '삭제'],
  'serviceorder.restore': ['서비스오더', '복구'],
  'serviceorder.restore_deleted': ['삭제된 서비스오더', '복구'],
  // 계획서(2026-08-27) 요구 2 — 원클릭 교체. 회수·계약 승계·새 배치가 한 동작이다.
  'deployment.swap': ['로봇', '교체'],
  // 2026-10-20 — 잘못한 배치 되돌리기(나가지 않은 것으로).
  'deployment.undo': ['로봇', '배치 되돌리기'],
  // 2026-10-21 — 잘못 누른 회수 되돌리기(다시 운영 중으로).
  'deployment.return.undo': ['로봇', '회수 되돌리기'],
  // 2026-10-26 — 나가 있는 배치에 스페어(예비 로봇) 표시를 켜고 끈다.
  'deployment.spare': ['배치', '스페어 표시'],
  // 2026-10-26 — 사업 대시보드 권역별 현황의 목표 · 보정 입력.
  'dashboard.business-region-input': ['권역별 현황', '목표 · 보정 입력'],
  'dashboard.field-issue-note': ['로봇 운영 이슈', '신규 · 누적 이슈 글 수정'],
  'serviceorder.severity_change': ['서비스오더 중요도', '변경'],
  'serviceorder.reassign_customer': ['서비스오더 고객사', '재지정'],
  'serviceorder.bulk_reassign_customer': ['서비스오더 고객사', '일괄 재지정'],
  'serviceorder.sla_recompute': ['서비스오더 응답 기한', '재계산'],
  'serviceorder.preventive_scan_run': ['예방 정비 점검', '실행'],

  // ── 고객 리포트 ──────────────────────────────────────────────────
  'customerreport.submitted': ['고객 리포트', '접수'],
  'customerreport.sla_breached': ['고객 리포트 응답 기한', '초과'],
  'report.update': ['리포트', '수정'],
  'report.delete': ['리포트', '삭제'],
  'report.restore': ['리포트', '복구'],
  'report.hard-delete': ['리포트', '완전삭제'],
  // 시트 수집 고객사 리포트(26-09-11). '접수'는 사람이 아니라 구글 시트 수집기가
  // 하는 일이라 '수집'으로 적는다 — 감사 화면에서 두 경로를 구분해야 한다
  // (customerreport.* 는 QR 스캔 경로).
  'customersheetreport.ingest': ['시트 고객사 리포트', '수집'],
  'customersheetreport.update': ['시트 고객사 리포트', '수정'],
  // 소프트 삭제 — 행은 남고 deletedAt 만 찍힌다. '완전삭제'(report.hard-delete)와
  // 구분해야 감사 화면에서 되돌릴 수 있는 건인지 알 수 있다.
  'customersheetreport.delete': ['시트 고객사 리포트', '삭제'],
  'customersheetreport.merge': ['시트 고객사 리포트', '같은 건 합치기'],
  'customersheetreport.restore': ['시트 고객사 리포트', '복구'],
  // 시트에 없어진 건을 콘솔에서도 치우는 일괄 처리(26-09-14). 한 번에 여러 건을
  // 지우므로 '삭제'와 같은 말을 쓰면 감사 화면에서 낱건 삭제와 구별되지 않는다.
  'customersheetreport.reconcile': ['시트 고객사 리포트', '시트와 대조(없어진 건 정리)'],

  // ── 고장 조치 이력 (26-09-14) ────────────────────────────────────────
  // 리포트(고객이 올린 신고)와 조치(현장이 한 일)는 서로 다른 표다 — 감사 화면에서
  // 섞이면 누가 무엇을 했는지 읽을 수 없어 말머리를 다르게 둔다.
  'faultremediation.ingest': ['고장 조치 이력', '수집'],
  'faultremediation.create': ['고장 조치 이력', '작성'],
  'faultremediation.update': ['고장 조치 이력', '수정'],
  'faultremediation.delete': ['고장 조치 이력', '삭제'],
  'faultremediation.restore': ['고장 조치 이력', '복구'],
  // 조치가 어느 신고에 대한 것인지 다시 이어 붙이는 일.
  'faultremediation.rematch': ['고장 조치 이력의 신고 연결', '다시 맞춤'],

  // ── 개인정보 요청(DSAR) ──────────────────────────────────────────
  'privacy-request.submitted': ['개인정보 요청', '접수'],
  'privacy-request.update': ['개인정보 요청', '처리'],
  'privacy-request.sla_breached': ['개인정보 요청 처리 기한', '초과'],

  // ── 첨부파일 ─────────────────────────────────────────────────────
  'attachment.request-upload': ['첨부', '업로드 요청'],
  'attachment.confirm': ['첨부', '확인'],
  'attachment.delete': ['첨부', '삭제'],
  'attachment.restore': ['첨부', '복구'],
  'attachment.hard-delete': ['첨부', '완전삭제'],
  'attachment.transfer-owner': ['첨부 소유', '이전'],

  // ── 공개(무인증) 경로 ────────────────────────────────────────────
  'public.attachment.request-upload': ['공개 첨부', '업로드 요청'],
  'public.attachment.confirm': ['공개 첨부', '확인'],
  'public.service-request.request-otp': ['공개 서비스 요청 인증번호', '요청'],
  'public.service-request.submit': ['공개 서비스 요청', '접수'],

  // ── 현장 방문 ────────────────────────────────────────────────────
  'field-visit.create': ['현장 방문', '등록'],
  'field-visit.update': ['현장 방문', '수정'],

  // ── 인프라 발주(설치 준비) ───────────────────────────────────────
  'infrastructure-order.delete': ['인프라 발주', '삭제'],
  'infrastructure-order.restore': ['인프라 발주', '복구'],
  'infrastructure-order.hard-delete': ['인프라 발주', '완전삭제'],
  'infrastructure-order.install-complete-cascade': ['인프라 발주 설치 완료', '연쇄 처리'],
  'infrastructure-order.install-date-changed': ['인프라 발주 설치일', '변경'],

  // ── 변경점(ECN) ──────────────────────────────────────────────────
  'ecn.create': ['변경점', '등록'],
  'ecn.update': ['변경점', '수정'],
  'ecn.delete': ['변경점', '삭제'],
  'ecn.hard-delete': ['변경점', '완전삭제'],

  // ── 로더 (차수1 이 restore·hard-delete 를 신설했다 — 부록 A-9) ────
  'loader.create': ['로더', '등록'],
  'loader.update': ['로더', '수정'],
  'loader.delete': ['로더', '삭제'],
  'loader.restore': ['로더', '복구'],
  'loader.hard-delete': ['로더', '완전삭제'],
  'loader.swap': ['로더', '교체'],
  'loader.detach': ['로더', '분리'],

  // ── 로더 개조(개조 카탈로그 적용) ────────────────────────────────
  'loaderRev.create': ['로더 개조', '등록'],
  'loaderRev.update': ['로더 개조', '수정'],
  'loaderRev.delete': ['로더 개조', '삭제'],
  'loaderRev.hard-delete': ['로더 개조', '완전삭제'],
  'loaderRev.apply': ['로더 개조', '적용'],
  'loaderRev.unapply': ['로더 개조', '해제'],

  // ── 로더 개조 대장(엑셀) 업로드 — docs/plan/loader-rev-excel-upload-2026-09 ──
  // 'loader.ledger-apply' 는 확정 트랜잭션이 **로더 한 대마다** 남기는 줄이다.
  // 요약 한 줄만 남기면 "이 로더는 어느 업로드가 바꿨나"를 되짚을 수 없다.
  'loaderRevUpload.request': ['대장 업로드', '시작'],
  'loaderRevUpload.parse': ['대장 업로드', '읽기'],
  'loaderRevUpload.confirm': ['대장 반영', '확정'],
  'loaderRevUpload.cancel': ['대장 업로드', '취소'],
  'loader.ledger-apply': ['로더', '대장 반영'],

  // ── 부품 ─────────────────────────────────────────────────────────
  'part-catalog.create': ['부품 카탈로그', '등록'],
  'part-catalog.update': ['부품 카탈로그', '수정'],
  'part-lot.create': ['부품 로트', '등록'],
  'part-lot.defect-status.update': ['부품 로트 불량 상태', '변경'],
  'part-lot.recall': ['부품 로트', '리콜'],
  'robot-part.install': ['로봇 부품', '장착'],
  'robot-part.remove': ['로봇 부품', '제거'],

  // ── 생산 계획 ────────────────────────────────────────────────────
  'production-plan.create': ['생산 계획', '등록'],
  'production-plan.update': ['생산 계획', '수정'],
  'production-plan.delete': ['생산 계획', '삭제'],
  'production-plan.restore': ['생산 계획', '복구'],
  'production-plan.hard-delete': ['생산 계획', '완전삭제'],

  // ── 공급사 ───────────────────────────────────────────────────────
  'supplier.create': ['공급사', '등록'],
  'supplier.update': ['공급사', '수정'],

  // ── 솔루션 ───────────────────────────────────────────────────────
  'solution.create': ['솔루션', '등록'],
  'store-solution.attach': ['매장 솔루션', '연결'],
  'store-solution.deactivate': ['매장 솔루션', '해제'],

  // ── 선택지 값(운영자가 늘리는 드롭다운 항목) ─────────────────────
  'option-value.create': ['선택지 값', '추가'],
  'option-value.archive': ['선택지 값', '보관'],
  'option-value.restore': ['선택지 값', '복구'],
  'option-value.hard-delete': ['선택지 값', '완전삭제'],

  // ── 메뉴 구성 ────────────────────────────────────────────────────
  'menuOffering.exported.csv': ['메뉴 구성 CSV 파일', '내보내기'],
  'menuOffering.exported.xlsx': ['메뉴 구성 엑셀 파일', '내보내기'],

  // ── 서명 문서 ────────────────────────────────────────────────────
  'signature-document.create': ['서명 문서', '등록'],
  'signature-document.cancel': ['서명 문서', '취소'],
  'signature-document.delete': ['서명 문서', '삭제'],
  'signature-document.restore': ['서명 문서', '복구'],
  'signature-document.hard-delete': ['서명 문서', '완전삭제'],
  'signature-document.refinalize': ['서명 문서', '재확정'],
  'signature-document.final-pdf-download': ['완료 서명 문서 PDF', '내려받기'],
  'signature-document.links-viewed': ['서명 링크', '열람'],
  'signature-document.offline-submit': ['오프라인 서명', '등록'],
  'signature-document.prefill-preview': ['미리 채운 문서', '미리보기'],
  'signature-document.prefill-saved': ['미리 채운 값', '저장'],
  'signature-document.signatory-update': ['서명자 정보', '수정'],
  // 2026-09-23 — 로봇 없이 만든 문서에 로봇을 나중에 정하거나 바꾼다.
  'signature-document.robots-update': ['서명 문서 로봇', '수정'],
  // 2026-09-30 — 통합 캘린더 끌어 옮기기 · 구글 캘린더 양방향 연동(앱스 스크립트).
  'infrastructure-order.calendar-move': ['인프라 발주 일정', '캘린더에서 옮김'],
  'service-order.calendar-move': ['유지보수 일정', '캘린더에서 옮김'],
  // 2026-10-19 — 통합 캘린더에서 회수 일정 추가 · 옮김.
  'customer.return-date.calendar-set': ['회수 일정', '캘린더에서 정함'],
  //    캘린더에서 유지보수 잡기 — 예방정비 오더 + 고장 조치 이력(진행 중)을 함께 만든다.
  'service-order.calendar-create': ['유지보수 일정', '캘린더에서 추가'],
  // 2026-10-20 — 통합 캘린더 우클릭 '삭제'.
  'infrastructure-order.calendar-delete': ['인프라 발주 일정', '캘린더에서 삭제(날짜 비움)'],
  'service-order.calendar-delete': ['유지보수 일정', '캘린더에서 삭제'],
  'customer.return-date.calendar-clear': ['회수 일정', '캘린더에서 삭제(날짜 비움)'],
  // 2026-10-20 — 통합 캘린더 로봇 교체 일정(설치 + 회수).
  'service-order.calendar-replacement-create': ['로봇 교체 일정', '캘린더에서 추가'],
  'service-order.calendar-replacement-complete': ['로봇 교체 일정', '캘린더에서 교체 완료'],
  'service-order.calendar-replacement-update': ['로봇 교체 일정', '캘린더에서 수정'],
  'service-order.calendar-replacement-cancel': [
    '로봇 교체 일정',
    '캘린더에서 삭제(작업 취소 · 교체 되돌리기)',
  ],
  //    구글 캘린더에서 사람이 옮기거나 지운 것 — 작성자 기록 없음(시스템이 받아 적는다).
  // 2026-10-22 — 재계약 일정(옛 고객사 → 새 고객사).
  'customer-renewal.create': ['재계약 일정', '캘린더에서 추가'],
  'customer-renewal.update': ['재계약 일정', '캘린더에서 수정'],
  'customer-renewal.complete': ['재계약 일정', '재계약 완료'],
  'customer-renewal.undo': ['재계약 일정', '재계약 되돌리기'],
  'customer-renewal.delete': ['재계약 일정', '일정 삭제'],
  // 2026-11-02 — 이벤트 일정(행사장 설치 · 회수)과 이벤트용 로봇 배정.
  'calendar-event.create': ['이벤트 일정', '캘린더에서 추가'],
  'calendar-event.update': ['이벤트 일정', '수정'],
  'calendar-event.delete': ['이벤트 일정', '일정 삭제'],
  'calendar-event.install': ['이벤트 일정', '설치 완료'],
  'calendar-event.undo-install': ['이벤트 일정', '설치 완료 되돌리기'],
  'calendar-event.return': ['이벤트 일정', '회수 완료'],
  'calendar-event.undo-return': ['이벤트 일정', '회수 완료 되돌리기'],
  'calendar-event.assign': ['이벤트 로봇 배정', '배정'],
  'calendar-event.release': ['이벤트 로봇 배정', '배정 해제'],
  'google-calendar.date-change': ['구글 캘린더 일정', '날짜 변경 반영'],
  // 2026-10-23 — 구글 시트 '데모-계약 전환' 탭(콘솔에서 고친 칸 · 새 줄 → 1분 안에 시트에 쓰인다).
  'sheet-mirror.edit': ['데모-계약 전환 시트', '칸 고침'],
  'sheet-mirror.append': ['데모-계약 전환 시트', '새 줄 추가'],
  'google-calendar.date-clear': ['구글 캘린더 일정', '삭제 반영(날짜 비움)'],

  // ── 서명 양식 ────────────────────────────────────────────────────
  'signature-template.create': ['서명 양식', '등록'],
  'signature-template.update': ['서명 양식', '수정'],
  'signature-template.duplicate': ['서명 양식', '복제'],
  'signature-template.archive': ['서명 양식', '보관'],
  'signature-template.restore': ['서명 양식', '복구'],
  'signature-template.hard-delete': ['서명 양식', '완전삭제'],
  'signature-template.restore-version': ['서명 양식 판본', '되돌리기'],
  'signature-template.version-cleanup': ['서명 양식 옛 판본', '정리'],
  // 서식 캔버스(2026-09-22) — 표·글·도형·사진으로 양식을 그리는 기능.
  'signature-template.build_layout': ['서명 양식 캔버스', '저장'],
  'signature-template.xlsx_to_layout': ['엑셀 서식', '캔버스로 가져오기'],
  'signature-layout-preset.create': ['캔버스 서식', '등록'],
  'signature-layout-preset.rename': ['캔버스 서식', '이름 변경'],
  'signature-layout-preset.archive': ['캔버스 서식', '삭제'],
  'signature-layout-asset.create': ['캔버스 사진', '등록'],
  'signature-layout-asset.archive': ['캔버스 사진', '삭제'],

  // ── 관리자 연쇄 완전삭제(퍼지) ───────────────────────────────────
  'admin.customer-purge.request': ['고객사 완전삭제', '요청'],
  'admin.customer-purge': ['고객사', '완전삭제'],
  'admin.contract-purge.request': ['계약 완전삭제', '요청'],
  'admin.contract-purge': ['계약', '완전삭제'],

  // ── 그 외 ────────────────────────────────────────────────────────
  'pii.exported': ['PII 데이터', '내보내기'],
  'self-check.list': ['자체 점검 기록', '열람'],
  // 화면에서 서버로 올려 보내는 유일한 감사 사건(PII 표시 토글).
  'ui.inbox.reporter_phone_visibility.toggle': ['접수자 연락처 표시', '전환'],
  // ── 감사 보강 ①-C(2026-08-28) — 기록이 아예 없던 변경 라우트 37곳에 @Audit 을
  //    붙이면서 함께 정한 표기. 오너 요구: "감사 기록은 관리자가 알아보기 쉽게
  //    한글로 표기 되어야 하며, 누가 언제 무엇을 어떻게가 기록되어야 합니다."
  //    목적어/서술어를 나눠 적는 이 파일의 규칙을 그대로 따른다.
  //    서비스오더 — 접수부터 종결까지 현장 대응의 본체.
  'serviceorder.create': ['서비스오더', '접수'],
  'serviceorder.transition': ['서비스오더', '상태 변경'],
  'serviceorder.cancel': ['서비스오더', '취소'],
  'serviceorder.bulk_assign': ['서비스오더', '일괄 담당 지정'],
  'serviceorder.bulk_transition': ['서비스오더', '일괄 상태 변경'],
  'serviceorder.batch_recall': ['서비스오더', '일괄 리콜 생성'],
  'serviceorder.replacement_robot': ['서비스오더 교체 로봇', '지정'],
  'serviceorder.part_add': ['서비스오더 부품', '추가'],
  'serviceorder.note_add': ['서비스오더 메모', '추가'],
  'serviceorder.signature_add': ['서비스오더 서명', '추가'],
  'serviceorder.photo_add': ['서비스오더 사진', '추가'],
  //    인수증 — 고객에게 로봇을 넘긴 법적 기록.
  'acceptance.create': ['인수증', '생성'],
  'acceptance.sign': ['인수증', '서명'],
  'acceptance.finalize': ['인수증', '확정'],
  'acceptance.photo_add': ['인수증 사진', '추가'],
  'acceptance.checklist_update': ['인수증 점검 항목', '수정'],
  'acceptance.signatory_add': ['인수증 서명자', '추가'],
  'acceptance.signatory_update': ['인수증 서명자', '수정'],
  'acceptance.signatory_delete': ['인수증 서명자', '삭제'],
  //    배정 — 계약에 로봇을 물리는 자리(예약이 풀리면 계약 연결이 끊긴다).
  'assignment.assign': ['계약 배정', '지정'],
  'assignment.assign_robot': ['로봇 배정', '지정'],
  //    그 밖의 운영 기록
  'firmware.record': ['펌웨어 이력', '기록'],
  'infrastructure-order.upsert': ['인프라 발주', '저장'],
  'infrastructure-order.request_contact': ['인프라 발주', '연락 요청'],
  'menu-offering.save': ['메뉴 구성', '저장'],
  'production-plan.allocate': ['제조 계획', '할당'],
  'production-plan.allocation_release': ['제조 계획 할당', '해제'],
  'production-plan.materialize': ['제조 계획', '로봇 생성'],
  'signature-template.upload_url': ['서명 서식 파일', '업로드 주소 발급'],
  'signature-template.upload_complete': ['서명 서식 파일', '업로드 완료'],
  'signature-template.asset_upload_url': ['서명 양식 캔버스 사진', '업로드 주소 발급'],
  'signature-template.xlsx_upload_url': ['엑셀 서식 파일', '업로드 주소 발급'],
  'signature-layout-asset.upload_url': ['캔버스 사진', '업로드 주소 발급'],
  'signature-document.attachment_upload_url': ['서명 문서 첨부', '업로드 주소 발급'],
  //    공개(무인증) 경로 — 작성자가 없다. 문장은 주어 없이 "(작성자 기록 없음)".
  'signature-document.public_submit': ['서명 문서', '고객 서명 제출'],
  'signature-document.public_decline': ['서명 문서', '고객 서명 거절'],
  'report.public_submit': ['고객 리포트', '고객 접수'],
  'privacy-request.submit': ['개인정보 요청', '접수'],
  'self-check.submit': ['셀프 체크', '제출'],
};

/**
 * **서버에 지금은 없지만 과거 기록에 남아 있을 수 있는** 액션.
 *
 * 감사 장부는 append-only 라 지난 행이 사라지지 않는다. 아래 다섯은 `git log -S`
 * 로 apps/api 에 실제로 존재했던 이력을 확인한 것들이라(=운영 DB 에 행이 있을 수
 * 있다) 라벨을 남긴다. 드리프트 시험이 이 목록을 **정확히** 허용 목록으로 쓰므로,
 * 여기 아무 키나 넣으면 "서버에 없는 라벨" 검사가 그만큼 무력해진다.
 *
 * ⚠ 이번 차수에 지운 stale 키 10개(auth.totp.*·deployment.update·serviceorder
 *   .create/update/cancel/transition/assign·privacy-request.fulfill/reject)는
 *   여기 오지 않는다 — apps/api 이력에 **한 번도 없었다**(git log -S 0건). 계획서를
 *   보고 미리 적어 둔 라벨이라 지우는 것이 맞다.
 */
export const LEGACY_ACTION_PARTS: Record<string, AuditActionParts> = {
  // 옛 QR 액션 접두사. 현재는 robot.qr.* 다.
  'robotqr.issue': ['QR', '발급'],
  'robotqr.revoke': ['QR', '폐기'],
  // 첨부 업로드 확정 액션의 옛 이름(현재 attachment.confirm).
  'attachments.upload': ['첨부', '업로드'],
  // 공개 리포트 제출. 현재는 큐 경유 customerreport.submitted 로 남는다.
  'public.report.submit': ['공개 리포트', '제출'],
  // 최초 부트스트랩(관리자 계정 생성). 도입 시점 1회.
  'system.bootstrap': ['시스템', '초기화'],
};

// 액션 문자열이 아예 모르는 것일 때의 조합 표기. 서버가 먼저 새 액션을 내보내도
// 화면이 영문 토큰을 그대로 뱉지 않게 하는 마지막 그물이다(드리프트 시험이
// 그 사이를 좁히지만, 배포 순서상 서버가 앞설 수 있다).
const ENTITY_NAMES: Record<string, string> = {
  auth: '인증',
  user: '사용자',
  robot: '로봇',
  robotqr: 'QR',
  store: '매장',
  customer: '고객사',
  deployment: '배치',
  assignment: '배정',
  serviceorder: '서비스오더',
  customerreport: '리포트',
  report: '리포트',
  'privacy-request': '개인정보 요청',
  attachment: '첨부파일',
  attachments: '첨부파일',
  contract: '계약',
  deal: '딜',
  'field-visit': '현장 방문',
  'robot-acceptance': '인수',
  acceptance: '인수증',
  'infrastructure-order': '인프라 발주',
  loader: '로더',
  loaderRev: '로더 개조',
  'part-catalog': '부품 카탈로그',
  'part-lot': '부품 로트',
  'robot-part': '로봇 부품',
  'production-plan': '생산 계획',
  'option-value': '선택지 값',
  'signature-document': '서명 문서',
  'signature-template': '서명 양식',
  solution: '솔루션',
  'store-solution': '매장 솔루션',
  'customer-note': '고객사 메모',
  'self-check': '자체 점검',
  admin: '관리자 작업',
  pii: 'PII',
  system: '시스템',
  public: '공개',
  'customer-master': '고객사 마스터',
  'active-customers': '활성 고객사',
  ecn: '변경점',
  supplier: '공급사',
  menuOffering: '메뉴 구성',
  ui: '화면 이벤트',
};

const VERB_NAMES: Record<string, string> = {
  create: '등록',
  update: '수정',
  delete: '삭제',
  'hard-delete': '완전삭제',
  'permanent-delete': '완전삭제',
  transfer: '이관',
  exported: '내보내기',
  issue: '발급',
  revoke: '폐기',
  sign: '서명',
  terminate: '해지',
  finalize: '확정',
  return: '반납',
  submit: '제출',
  submitted: '접수',
  reject: '반려',
  cancel: '취소',
  restore: '복구',
  closed: '종료',
  transition: '상태 변경',
  assign: '배정',
  release: '해제',
  confirm: '확인',
  upload: '업로드',
  apply: '적용',
  unapply: '해제',
  archive: '보관',
  duplicate: '복제',
  detach: '분리',
  swap: '교체',
  recall: '리콜',
  install: '장착',
  remove: '제거',
  attach: '연결',
  deactivate: '해제',
  bootstrap: '초기화',
  list: '열람',
  view: '열람',
  toggle: '전환',
};

/** 알려진 액션(정본 + 레거시)의 조각. 모르면 null. */
export function auditActionParts(action: string): AuditActionParts | null {
  return ACTION_PARTS[action] ?? LEGACY_ACTION_PARTS[action] ?? null;
}

/**
 * 액션 문자열 → 표의 '액션' 칸에 쓰는 짧은 한국어 이름.
 *
 * @example
 *   formatAuditLogAction('serviceorder.closed')  // → "서비스오더 종료"
 *   formatAuditLogAction('robot.qr.revoke')      // → "QR 폐기"
 *   formatAuditLogAction('new-entity.create')    // → "new-entity 등록" (조합 폴백)
 */
export function formatAuditLogAction(action: string): string {
  if (!action) return '–';

  const parts = auditActionParts(action);
  if (parts) return joinParts(parts);

  // 조합 폴백 — 2·3토막 액션을 엔티티 + 동작으로 읽는다.
  const segs = action.split('.');
  if (segs.length >= 2) {
    const entity = ENTITY_NAMES[segs[0]] ?? segs[0];
    const verbKey = segs.length === 3 ? `${segs[1]}.${segs[2]}` : segs[1];
    const verb =
      VERB_NAMES[verbKey] ?? VERB_NAMES[segs[segs.length - 1]] ?? segs.slice(1).join('.');
    return `${entity} ${verb}`;
  }
  return action;
}

function joinParts([object, verb]: AuditActionParts): string {
  return object ? `${object} ${verb}` : verb;
}

// ============================================================================
// ② 한국어 문장
// ============================================================================

/** 이름을 못 찾은 작성자(계정이 지워진 뒤의 기록). */
export const DELETED_ACTOR_LABEL = '(삭제된 계정)';
/** 작성자 자체가 기록되지 않은 행(시스템 작업·무인증 공개 요청). */
export const UNKNOWN_ACTOR_LABEL = '—';

/**
 * 개선 9건 차수2c(26-08-04) 결함③ — **변경 내용(diff)을 싣는 경로의 정본.**
 *
 * 서버에서 `setAuditDiff()` 를 부르는 경로가 곧 이 목록이다(전수 — 10개 모듈의
 * 15개 호출부에서 나오는 18개 액션). 문장 렌더러는 diff 가 비었을 때 이 목록을
 * 보고 꼬리말을 가른다:
 *   · 목록에 있는데 diff 가 없다 → "(바뀐 내용이 기록되지 않았습니다)"
 *     — 이 경로는 원래 내용을 싣는다. 안 실린 이유는 이 행이 저장 확대 이전에
 *       쌓였거나(과거 기록) 인터셉터가 실패 응답의 diff 를 버렸기 때문이다.
 *       둘을 구분할 재료가 행에 없으므로 문장은 **사실만** 말한다.
 *   · 목록에 없다 → "(이 작업은 변경 내용을 기록하지 않습니다)"
 *     — 시기 문제가 아니라 그 경로의 성질이다. 오늘 만든 기록도 그렇다.
 *
 * ⚠️ **서버가 저장을 확대하면 이 목록도 자라야 한다.** 잊으면 오늘 실린 diff 를
 *   두고 문장이 "기록하지 않습니다"라고 말하는(또는 그 반대의) 거짓이 난다. 그래서
 *   audit-log-format.test.ts 에 **드리프트 시험**을 붙였다 — 서버 원문에서
 *   `setAuditDiff` 호출부를 세어 이 목록과 대조하므로, 새 호출부가 생기면 시험이
 *   먼저 깨져 "목록에 넣을지 정하라"고 알려 준다.
 *
 * 액션 이름의 근거(코드로 확인) — 각 `setAuditDiff` 호출부가 속한 서비스 메서드를
 * 부르는 라우트의 `@Audit({action})` 이다. 예: contracts.service 의 세 호출부는
 * contract.update · contract.reschedule_install · contract.terminate.
 */
export const DIFF_CARRYING_ACTIONS: ReadonlySet<string> = new Set([
  // 같은 건 합치기(2026-11-03) — 지운 번호 · 남긴 번호 · 옮긴 방문 일정 · 조치 이력 수.
  'customersheetreport.merge',
  // 시트 양방향 연동(2026-11-03) — 시트에서 고친 요약 · 상태가 콘솔에 들어온 수집.
  'customersheetreport.ingest',
  // 로봇 기준일 마감본(2026-10-08) — 마감본 CSV 내보내기는 기준일·행 수를, 관리자 '지금 마감'은
  // 기준일·출처·행 수를 싣는다(지금 목록 CSV 내보내기는 diff 없음 — '내보내기'는 고치는 동작이 아니라
  // 꼬리말이 붙지 않는다).
  'robot.exported',
  'robot-snapshot.capture',
  // loaders.service — update(2026-09-04 부터 diff 를 싣는다. 종전엔 누가·언제만 남아
  // 대장 업로드가 "콘솔이 비고를 뭐라고 고쳤나"를 말할 수 없었다.)
  'loader.update',
  // loader-rev-uploads.service — 확정 요약(건수·결정) + 로더 한 대마다 한 줄.
  'loaderRevUpload.confirm',
  // contracts.service — update · rescheduleInstall · terminate
  'contract.update',
  'contract.reschedule_install',
  'contract.terminate',
  // renewal-review.service — decide(이전 판정 → 새 판정. 재판정이 허용되므로 '무엇에서
  // 무엇으로'가 남아야 한다)
  'contract-renewal.review',
  // customer-master.service — recordRowDiff 를 부르는 두 경로(update · 상태 전이)
  'customer-master.update',
  'customer-master.status.update',
  // customer-notes.service — update · remove(하드 삭제 전 본문 보존)
  'customer-note.update',
  'customer-note.delete',
  // deployments.service — create · return (차수2b 저장 확대)
  'deployment.create',
  'deployment.return',
  // ecns.service — hardDelete(사라지는 코드·참조 SO 를 남긴다)
  'ecn.hard-delete',
  // robots.service — update(차수2b 저장 확대) · correctStatus
  'robot.update',
  'robot.status-correction',
  // robots.service — correctContractKind · linkContract · unlinkContract
  // (2026-09-01, plan retire-rollback-sale-kind-2026-09 §2-5·§2-2)
  'robot.contract-kind-correction',
  'robot.contract-link',
  'robot.contract-unlink',
  // robots.service — 이력 편집 세 경로(2026-11-02): 숨긴 줄 · 상태 전후
  'robot.history-rewind',
  'robot.history-hide',
  'robot.history-revert',
  // signature-documents.service — updateSignatory · updateRobots(2026-09-23)
  'signature-document.signatory-update',
  'signature-document.robots-update',
  // schedule-dates.controller — 통합 캘린더 끌어 옮기기(2026-09-30)
  'infrastructure-order.calendar-move',
  'service-order.calendar-move',
  'customer.return-date.calendar-set',
  // 2026-10-20 — 통합 캘린더 우클릭 '삭제'(비운 날짜 · 지운 표시를 diff 로).
  'infrastructure-order.calendar-delete',
  'service-order.calendar-delete',
  'customer.return-date.calendar-clear',
  // 2026-10-22 — 재계약 완료(회수 · 이어서 사용 · 설치한 로봇) · 되돌리기 · 일정 삭제.
  'customer-renewal.complete',
  'customer-renewal.undo',
  'customer-renewal.delete',
  // 2026-11-02 — 이벤트 일정 삭제 · 설치 완료 · 회수 완료 · 되돌리기.
  'calendar-event.delete',
  'calendar-event.install',
  'calendar-event.undo-install',
  'calendar-event.return',
  'calendar-event.undo-return',
  // 2026-10-23 — 시트 탭 거울: 고친 칸(전 → 후) · 새 줄 값.
  'sheet-mirror.edit',
  'sheet-mirror.append',
  // stores.service — update (차수2b 저장 확대)
  'store.update',
  // customers/retired-override.helper (계획서 요구 2a §2·§7ⓐ, 26-08-21) — 재계약 자동
  // 복귀: 새 계약이 ACTIVE 가 되면 '종료' 수동 고정(manualStatusOverride='retired')을
  // 풀고 그 표식을 diff 로 남긴다. 헬퍼는 계약이 ACTIVE 가 되는 7경로의 트랜잭션 안에서
  // 불리고, 감사 행의 액션은 **부르는 라우트**의 것이다 — 그래서 아래 세 액션이 새로
  // diff 를 실을 수 있게 됐다(나머지 경로의 customer-master.update · status.update 는
  // 이미 위 목록에 있다).
  'contract.sign',
  'customer-master.create',
  'customer.restore',
  // ── 감사 보강 ①-B(2026-08-28) — 완전삭제 스냅샷 ──────────────────────────
  // 오너 조건: 권한을 여는 대신 "누가 삭제했는지"가 로그에 남아야 한다. 조사해 보니
  // 완전삭제 12경로 중 **내용을 남기는 곳은 ecn 한 곳뿐**이었고 나머지는 "누가 지웠다"
  // 만 남아 무엇을 잃었는지 알 수 없었다. 이제 전 경로가 setDeleteSnapshot 으로
  // 삭제 직전 내용을 남긴다(모양은 필드 diff 가 아니라 { deleted, sideEffects } 봉투).
  'attachment.hard-delete',
  'deal.hard-delete',
  'infrastructure-order.hard-delete',
  'loader.hard-delete',
  'loaderRev.hard-delete',
  'option-value.hard-delete',
  'production-plan.hard-delete',
  'report.hard-delete',
  'robot.permanent-delete',
  'signature-document.hard-delete',
  'signature-template.hard-delete',
  // ── 감사 보강 ①-C(2026-08-28) — 회수 완료도 바뀐 값을 남긴다 ──────────────
  // 종전엔 이 경로만 diff 가 비어 "…회수를 처리 (바뀐 내용이 기록되지 않았습니다)"
  // 로 떴다. 같은 일을 로봇 상세에서 하면(deployment.return) 값이 남는데 활성
  // 고객사 화면에서 하면 안 남는, **어느 화면에서 눌렀는지에 따라 기록이 달라지는**
  // 상태였다. 두 경로가 같은 정본(applyRobotReturn)을 쓰므로 기록도 맞춘다.
  'active-customers.return.complete',
  // 계획서(2026-08-27) 요구 2 — 원클릭 교체. 두 로봇이 함께 움직이므로 diff 에
  // '어느 로봇에서 어느 로봇으로' 가 실린다.
  'deployment.swap',
  // 2026-10-20 — 배치 되돌리기도 어느 로봇이 어떤 상태로 돌아갔는지 남긴다.
  'deployment.undo',
  // 2026-10-21 — 회수 되돌리기도 회수일 · 사유가 어떻게 지워졌는지 남긴다.
  'deployment.return.undo',
  // 2026-10-26 — 스페어 표시를 켰는지 껐는지 남긴다.
  'deployment.spare',
  // 2026-10-26 — 권역별 현황 목표 · 보정 값이 무엇에서 무엇으로 바뀌었는지 남긴다.
  'dashboard.business-region-input',
  // 2026-10-28 — 신규 · 누적 이슈 글을 무엇에서 무엇으로 고쳤는지 남긴다.
  'dashboard.field-issue-note',
]);

/**
 * diff 를 싣는 경로인데 이 기록에는 내용이 없다(과거 행 또는 실패 응답).
 * '시기'를 말하지 않는다 — 행이 그것을 알려 주지 않으므로.
 */
export const DIFF_MISSING_NOTE = '(바뀐 내용이 기록되지 않았습니다)';
/** 애초에 변경 내용을 싣지 않는 경로. 시기와 무관한 **경로의 성질**이다. */
export const NO_DIFF_PATH_NOTE = '(이 작업은 변경 내용을 기록하지 않습니다)';

/**
 * 작성자 표기 한 벌. 감사 화면·로봇 타임라인이 **같은 이 판정**을 쓴다.
 *
 * - 이름이 풀린다            → 그 이름
 * - id 는 있는데 이름이 없다 → '(삭제된 계정)'  (계정이 완전삭제된 뒤의 기록)
 * - id 자체가 없다           → '—'             (시스템 작업·무인증 공개 요청)
 */
export function formatAuditActorLabel(
  actorUserId: string | null | undefined,
  displayName: string | null | undefined,
): string {
  if (displayName) return displayName;
  return actorUserId ? DELETED_ACTOR_LABEL : UNKNOWN_ACTOR_LABEL;
}

export interface AuditSentenceInput {
  action: string;
  actorUserId?: string | null;
  /** 서버가 풀어 준 작성자 이름(`AuditLogDto.actor.displayName`). */
  actorDisplayName?: string | null;
  /**
   * 대상의 이름("백암한옥구이"). 감사 행 자체에는 대상 이름 칸이 없으므로
   * (AuditLog 는 resourceType + resourceId 만 남긴다) 아는 호출부만 넘긴다.
   * 없으면 문장은 종류까지만 말한다 — 없는 이름을 지어내지 않는다.
   */
  resourceLabel?: string | null;
  /** AuditLog.diff 원문. 필드단위 diff 면 문장이 값까지 말한다. */
  diff?: unknown;
}

/**
 * 감사 기록 한 줄을 한국어 문장으로.
 *
 * 세 갈래다(계획서 §3 의 예시 3종과 1:1):
 *   ① 변경 전/후가 있는 수정
 *      "홍길동님이 백암한옥구이의 보증금을 ₩500,000 → ₩1,000,000으로 변경"
 *   ② 수정이 아닌 기록
 *      "홍길동님이 매장 ○○점을 등록"
 *   ③ 바뀐 내용을 말할 수 없는 수정 — **경로의 성질**로 두 갈래(차수2c 결함③)
 *      "홍길동님이 로봇 정보를 수정 (바뀐 내용이 기록되지 않았습니다)"
 *        = diff 를 싣는 경로인데 이 행에는 없다(과거 행 · 실패 응답)
 *      "홍길동님이 로더를 수정 (이 작업은 변경 내용을 기록하지 않습니다)"
 *        = 그 경로가 애초에 diff 를 싣지 않는다(오늘 기록도 마찬가지)
 *
 * 문장은 **저장된 것 이상을 말하지 않는다.** 그래서 ③ 이 있고, 작성자를 모르면
 * 이름을 지어내지 않고 '(작성자 기록 없음)' 을 꼬리에 붙인다.
 */
export function formatAuditLogSentence(input: AuditSentenceInput): string {
  const { action, actorUserId, actorDisplayName, resourceLabel, diff } = input;

  // 주어. 이름을 아는 사람만 '님이', 삭제된 계정은 '이', 아예 모르면 주어 없이
  // 꼬리말로 밝힌다. 갈래는 **입력**으로 가른다 — 라벨 문자열을 되비교하면
  // 이름이 우연히 '(삭제된 계정)' 인 계정에서 뜻이 뒤집힌다.
  let subject = '';
  let tail = '';
  if (actorDisplayName) subject = `${actorDisplayName}님이 `;
  else if (actorUserId) subject = `${DELETED_ACTOR_LABEL}이 `;
  else tail = ' (작성자 기록 없음)';

  const parts = auditActionParts(action);
  const fieldDiff = asFieldDiff(diff);

  // 대상 이름은 두 곳에서 온다. 순서가 뜻이다:
  //
  //   ① resourceLabel — 서버가 응답을 만들 때 대상을 조회해 붙인 **현재 이름**.
  //      (2026-08-31 오너 지적 "어떤 로봇에 대한 변경 기록인지 불분명함" 의 수리로
  //       감사 목록·상세가 이제 이 칸을 채워 넘긴다.)
  //   ② 삭제 스냅샷 — 대상이 **완전삭제**되어 조회할 행이 없을 때, 지우기 직전의
  //      식별 칸이 diff 안에 남아 있다(감사 보강 ①-B, 2026-08-28). 이때 ①은 null 이다.
  //
  // ①을 먼저 보는 이유: 살아 있는 대상은 이름이 바뀌었을 수 있고, 사람이 지금
  // 화면에서 찾을 수 있는 이름이 현재 이름이다. 둘 다 없으면 **이름을 지어내지 않고**
  // 문장이 종류까지만 말한다("…님이 로봇을 수정").
  const snapshot = asDeleteSnapshot(diff);
  const label = resourceLabel ?? deleteSnapshotLabel(snapshot) ?? undefined;

  // ── ① 변경 전/후가 실린 수정 ─────────────────────────────────────
  if (fieldDiff && Object.keys(fieldDiff).length > 0) {
    const owner = label ? `${label}의 ` : '';
    return `${subject}${owner}${describeChanges(fieldDiff, action)}${tail}`;
  }

  // 목적어 + 서술어. 알 수 없는 액션은 조합 표기를 목적어 없이 그대로 쓴다.
  // 대상 이름은 **목적어가 있을 때만** 뒤에 붙인다("매장 ○○점을") — 목적어 없는
  // 사건(로그인 등)에 이름을 끼우면 뜻이 없는 문장이 된다.
  const verb = parts ? parts[1] : formatAuditLogAction(action);
  const objectNoun = parts?.[0] ? [parts[0], label].filter(Boolean).join(' ') : null;
  const predicate = objectNoun ? `${objectNoun}${objectParticle(objectNoun)} ${verb}` : verb;

  // ── ③ 바뀐 내용을 말할 수 없는 수정 ──────────────────────────────
  // diff 가 아예 없고(=null/undefined) 동작이 '고치는' 계열이면, 무엇이 바뀌었는지
  // 말할 수 없다는 사실을 문장이 밝힌다.
  //
  // 개선 9건 차수2c(26-08-04) 결함③ — 그 사실을 **'시기'가 아니라 '경로'로** 말한다.
  // 이 경로가 원래 diff 를 싣는가(DIFF_CARRYING_ACTIONS)로 두 꼬리말을 가른다.
  // 종전 한 문구("기록되지 않던 시기입니다")는 diff 를 애초에 싣지 않는 경로의
  // **오늘 기록**에도 붙어, 없는 '시기'를 지어내고 있었다.
  if ((diff === null || diff === undefined) && MUTATION_VERBS.has(verb)) {
    const note = DIFF_CARRYING_ACTIONS.has(action) ? DIFF_MISSING_NOTE : NO_DIFF_PATH_NOTE;
    return `${subject}${predicate} ${note}${tail}`;
  }

  // 빈 필드 diff({}) — 서버가 "호출은 됐고 바뀐 값은 없다"를 일부러 남긴 경우.
  if (fieldDiff) {
    return `${subject}${predicate} (바뀐 값이 없습니다)${tail}`;
  }

  // ── ② 그 밖의 기록 ───────────────────────────────────────────────
  return `${subject}${predicate}${tail}`;
}

/**
 * 완전삭제 스냅샷에서 **대상을 가리키는 한 값**을 고른다.
 *
 * 우선순위는 "운영자가 그것을 부르는 이름" 순이다 — 로봇은 짧은 시리얼로,
 * 개조·변경점은 코드로, 영업기회는 번호로 부른다. 없으면 null 을 돌려주고
 * 문장은 종전대로 목적어만 말한다 — **이름을 지어내지 않는다.**
 */
const IDENTIFYING_SNAPSHOT_FIELDS = [
  'serialShort',
  'serial',
  'code',
  'humanId',
  'displayId',
  'originalFilename',
  'name',
  'displayName',
  'value',
] as const;

function deleteSnapshotLabel(snapshot: DeleteSnapshotView | null): string | null {
  if (!snapshot) return null;
  for (const field of IDENTIFYING_SNAPSHOT_FIELDS) {
    const hit = snapshot.deleted.find((row) => row.field === field);
    if (!hit) continue;
    if (typeof hit.value === 'string' && hit.value.trim() !== '') return hit.value;
    if (typeof hit.value === 'number') return String(hit.value);
  }
  return null;
}

/** '고치는' 동작 — diff 가 없으면 ③ 꼬리말을 붙일 대상. */
const MUTATION_VERBS = new Set(['수정', '변경', '정정', '처리', '저장']);

/** 필드단위 diff → "보증금을 A → B로 변경" 목록. */
function describeChanges(
  fieldDiff: Record<string, { from: unknown; to: unknown }>,
  action: string,
): string {
  const entries = Object.entries(fieldDiff);
  const MAX_SPELLED_OUT = 2;
  const spelled = entries.slice(0, MAX_SPELLED_OUT).map(([field, change]) => {
    const label = formatDiffFieldLabel(field, action);
    const from = formatDiffValue(change.from, field);
    const to = formatDiffValue(change.to, field);
    return `${label}${objectParticle(label)} ${from} → ${to}${toParticle(to)}`;
  });
  const rest = entries.length - spelled.length;
  const more = rest > 0 ? ` 외 ${rest}건` : '';
  return `${spelled.join(', ')}${more} 변경`;
}

// ── 조사 ──────────────────────────────────────────────────────────────
// 받침 여부로 을/를 · 으로/로 를 고른다. 한글이 아닌 꼬리(영문 약어·숫자)는
// 읽는 소리로 판정한다 — 'QR을'(알), '₩1,000,000으로'(영).

/** 마지막 글자에 받침이 있는가. 판정 불가면 null(= 호출부가 기본값을 쓴다). */
function hasFinalConsonant(text: string): boolean | null {
  const last = text.trim().slice(-1);
  if (!last) return null;
  const code = last.charCodeAt(0);
  // 한글 음절 — (코드 - 0xAC00) % 28 이 0 이면 받침 없음.
  if (code >= 0xac00 && code <= 0xd7a3) return (code - 0xac00) % 28 !== 0;
  // 숫자 — 한국어 읽기의 마지막 소리 기준(0 영·1 일·3 삼·6 육·7 칠·8 팔 = 받침).
  if (last >= '0' && last <= '9') return '0136780'.includes(last);
  // 영문 약어 — 알파벳 이름의 마지막 소리 기준(L·M·N·R = 받침).
  const upper = last.toUpperCase();
  if (upper >= 'A' && upper <= 'Z') return 'LMNR'.includes(upper);
  return null;
}

function objectParticle(text: string): string {
  return hasFinalConsonant(text) === false ? '를' : '을';
}

function toParticle(text: string): string {
  const final = hasFinalConsonant(text);
  if (final === false) return '로';
  // 'ㄹ' 받침은 '로' 지만, 값 문자열에서 그 판정은 과하다 — 기본은 '으로'.
  return '으로';
}

// ============================================================================
// ③ diff 필드명
// ============================================================================
//
// 서버가 setAuditDiff() 로 남기는 키와 1:1. 호출 지점 6곳의 키를 전수 수록한다
// (고객사 마스터 projectForAudit · 고객사 메모 body · 변경점 완전삭제 · 로봇 상태
//  정정 · 서명자 이름/연락처). 미등록 키는 원문 그대로 노출되므로 안전하지만,
// 드리프트 시험이 "서버 키에 라벨이 다 있는가"를 지킨다.
const DIFF_FIELD_LABELS: Record<string, string> = {
  // 고객사 리포트 같은 건 합치기(2026-11-03)
  reportNo: '리포트 번호',
  visitServiceOrder: '방문 일정',
  reportStatus: '리포트 상태',
  reportSummary: '리포트 요약',
  movedRemediations: '옮긴 조치 이력(건)',
  // 고객사 마스터
  displayName: '상호',
  legalName: '법인명',
  segment: '세그먼트',
  countryCode: '국가',
  isShowing: '쇼잉',
  address: '위치',
  addressMapUrl: '지도 URL',
  hasGrillTeam: '그릴팀 보유',
  isSoleTrader: '개인사업자',
  hasInsurance: '보증 보험',
  hasGrillerSeparate: '그릴러 분리',
  rentalCompany: '렌탈사',
  expectedStoreCount: '매장 수',
  salesOwnerCode: '영업 담당',
  primaryContactName: '담당자',
  primaryContactPosition: '직함',
  primaryContactPhone: '연락처',
  primaryContactEmail: '이메일',
  notes: '비고',
  // 예전 고객사 단위 Store ID(2026-05-30 줄 단위 ContractLine.storeId 로 대체). 상세 화면과 같은 이름.
  profileConfig: 'Store ID (이전 입력)',
  // 계약
  contractType: '계약 형태',
  contractedAt: '계약일',
  endDate: '반납일',
  rentalTermMonths: '계약 기간(개월)',
  subscriptionStatus: '구독 상태',
  depositKrw: '보증금',
  // 개선 9건 차수2(26-08-04) §7 — 지원금·소개비. 서버(customer-master.service
  // projectForAudit)가 이미 diff 에 싣고 있고, 라벨이 없으면 감사 표에 영문 키가
  // 그대로 노출된다.
  subsidyKrw: '지원금',
  referralFeeKrw: '소개비',
  // 간택기(가스)
  grillSelectorType: '간택기 타입',
  grillSelectorInstall: '간택기 설치',
  // 진행 상태
  status: '진행 상태',
  returnReason: '회수 사유',
  isSpare: '스페어',
  robotSummary: '도입 로봇 요약',
  // 계획서 요구 2a §2·§7ⓐ(26-08-21) — 재계약 자동 복귀가 남기는 표식
  // (customers/retired-override.helper: { from: 'retired', to: null }). 칸의 뜻은
  // '진행 상태의 수동 고정' — 화면의 '고정' 배지·'자동 계산으로 되돌리기'와 같은 개념이라
  // 그 어휘를 따른다. 값은 상태 enum 이므로 ENUM_VALUE_LABELS 가 번역한다('retired'→'종료',
  // 풀림은 null → '–').
  manualStatusOverride: '진행 상태 고정',
  // 로봇 상태 정정
  locationCategory: '위치 분류',
  // 구분 수동 정정(plan 2-5) — 수동값(null=자동)과 사후 연결(plan 2-2)의 대상 계약 id.
  contractKindOverride: '구분 수동값',
  reservedContractId: '연결 계약(ID)',
  // 고객사 메모
  body: '메모 내용',
  // 서명자
  name: '이름',
  phone: '연락처',
  // 변경점 완전삭제(필드 diff 가 아니라 요약 payload — 표는 raw JSON 으로 접지만
  // 문장 쪽에서 키를 만나므로 라벨을 둔다)
  code: '변경점 코드',
  referencedServiceOrderCount: '참조 서비스오더 수',
  referencedServiceOrderIds: '참조 서비스오더',

  // ── 개선 9건 차수2b(26-08-04) 결정 ⑥A — 저장 확대로 새로 남는 키 ─────
  // 종전엔 아래 네 경로가 "누가 언제 건드렸다"만 남겼다. 서버가 diff 를 싣기
  // 시작했으니 라벨이 없으면 감사 표에 영문 키가 그대로 노출된다.
  //
  // 로봇 일반 편집 (robots.service projectRobotForAudit)
  serialShort: 'Short SN',
  serialLong: 'Long SN',
  modelCode: '모델',
  verificationStatus: '자산 확인',
  accountingBookNo: '회계번호',
  accountingScheme: '회계 구분',
  color: '색상',
  variantValue: '구분 사양',
  manufactureCompletedAt: '제조 완료일',
  binaryUpdatedAt: '바이너리 업데이트일',
  firstProductionYm: '첫 제조 월',
  firstShipmentYm: '첫 출고 월',
  // 배치 / 회수 (deployments.service)
  // 로봇 상태·위치를 'status'/'locationCategory' 가 아닌 제 이름으로 담는 이유:
  // 배치 감사 행의 주인은 Deployment 인데 그 두 칸은 **로봇**의 값이라, 같은 키를
  // 쓰면 액션별 예외표가 또 한 겹 필요해지고 뜻이 흐려진다.
  robotStatus: '로봇 상태',
  robotLocation: '로봇 위치',
  storeName: '배치 매장',
  customerName: '배치 고객사',
  kind: '배치 종류',
  reason: '배치 사유',
  deployedAt: '배치일',
  // 원클릭 교체(2026-08-27) — 교체 전/후 로봇을 시리얼로 말한다.
  robotSerial: '로봇 시리얼',
  // 2026-10-20 — 배치 되돌리기.
  undone: '배치 되돌림',
  undoReason: '되돌린 까닭',
  eventName: '행사명',
  returnedAt: '회수일',
  installedAt: '설치 완료',
  // 계약 (contracts.service)
  startDate: '시작일',
  // ⚠ 고객사 마스터의 rentalTermMonths 와 **같은 뜻**이고 키만 다르다(한쪽은 파생
  //   값, 한쪽은 Contract 컬럼). 라벨을 같게 두는 것이 운영자에게 옳다.
  durationMonths: '계약 기간(개월)',
  renewalScheduledAt: '갱신 예정일',
  scheduledInstallAt: '설치 예정일',
  terminationReason: '해지 사유',
  // 매장 (stores.service)
  branchName: '지점명',
  addressLine1: '주소',
  city: '시/군/구',
  region: '시/도',
  postalCode: '우편번호',
  country: '국가',
  active: '사용',
  parentStoreId: '상위 매장',
  customerId: '고객사',
};

/**
 * 액션마다 뜻이 다른 키의 예외표.
 *
 * `status` 가 그렇다 — 고객사 마스터에서는 '진행 상태'(계약 체결중/설치 완료…)이고
 * 로봇 상태 정정에서는 '로봇 상태'(정비 중/운영 중…)다. 같은 글자로 보여 주면
 * 운영자가 다른 것을 같은 것으로 읽는다.
 */
const ACTION_SCOPED_DIFF_FIELD_LABELS: Record<string, Record<string, string>> = {
  'robot.status-correction': { status: '로봇 상태' },
  // 개선 9건 차수2b(26-08-04) — 저장 확대로 같은 함정이 세 군데 더 생겼다.
  // 로봇 일반 편집도 같은 'status' 키를 쓴다(고객사 진행 상태가 아니다).
  'robot.update': { status: '로봇 상태' },
  // 서명 문서 로봇 수정 — 'robots' 는 대표(첫) 로봇부터 늘어놓은 시리얼 목록.
  'signature-document.robots-update': { robots: '로봇' },
  // 계약 경로의 'status' 는 계약 상태(초안·활성·만료·해지)다.
  'contract.update': { status: '계약 상태' },
  'contract.terminate': { status: '계약 상태' },
  // 매장의 'name' 은 매장명이다 — 서명자 정보 수정의 '이름'과 키가 겹친다.
  'store.update': { name: '매장명' },
};

// ============================================================================
// ④-2 완전삭제 스냅샷 — "무엇이 사라졌는가"
// ============================================================================
//
// 감사 보강 ①-B(2026-08-28). 서버가 완전삭제 직전에 남기는 봉투:
//   { deleted: { <칸>: <값> }, sideEffects?: { <칸>: <값> } }
//
// 필드 diff(`{칸:{from,to}}`) 와 **일부러 다른 모양**이다. 완전삭제는 "값이 바뀐
// 사건"이 아니라 "행이 통째로 없어진 사건"이라 같은 표에 담으면 뜻이 어긋난다
// (특히 곁가지 — 참조 서비스오더는 사라지지 않고 고아가 될 뿐이다).
//
// 오너 요구(2026-08-28): "감사 기록은 관리자가 알아보기 쉽게 한글로 표기 되어야
// 하며, 누가 언제 무엇을 어떻게가 기록되어야 한다."
//   · 누가   = AuditLog.actorUserId (문장 앞머리)
//   · 언제   = AuditLog.occurredAt
//   · 무엇을 = resourceType/resourceId + **이 스냅샷**
//   · 어떻게 = action (완전삭제)
// 이 파일은 그중 '무엇을'을 한국어로 옮기는 책임을 진다.

export interface DeleteSnapshotView {
  deleted: Array<{ field: string; value: unknown }>;
  sideEffects: Array<{ field: string; value: unknown }>;
}

/**
 * 완전삭제 스냅샷 봉투인지 판정하고 표로 그릴 수 있게 펼친다.
 * 봉투가 아니면 null — 호출 측은 종전대로 필드 diff / 날 JSON 경로로 간다.
 */
export function asDeleteSnapshot(diff: unknown): DeleteSnapshotView | null {
  if (!diff || typeof diff !== 'object' || Array.isArray(diff)) return null;
  const obj = diff as Record<string, unknown>;
  const deleted = obj.deleted;
  if (!deleted || typeof deleted !== 'object' || Array.isArray(deleted)) return null;
  // 봉투는 이 두 칸만 갖는다. 다른 칸이 섞여 있으면 다른 뜻의 객체다 — 지어내지 않는다.
  const keys = Object.keys(obj);
  if (keys.some((k) => k !== 'deleted' && k !== 'sideEffects')) return null;
  const side = obj.sideEffects;
  const spread = (v: unknown) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.entries(v as Record<string, unknown>).map(([field, value]) => ({ field, value }))
      : [];
  return { deleted: spread(deleted), sideEffects: spread(side) };
}

/**
 * 스냅샷 칸 이름 — 여러 자원이 함께 쓰는 뜻이 분명한 키들.
 * 자원마다 뜻이 갈리는 키(status·code·name·seq·value…)는 아래 액션별 표가 덮는다.
 */
const DELETE_SNAPSHOT_FIELD_LABELS: Record<string, string> = {
  // 공통 계보·시각
  createdAt: '등록 시각',
  deletedAt: '삭제 표시 시각',
  archivedAt: '보관 시각',
  completedAt: '완료 시각',
  ownerEntity: '소유 법인',
  createdById: '등록자',
  // 연결
  customerId: '고객사',
  storeId: '매장',
  contractId: '계약',
  robotId: '로봇',
  modelId: '기종',
  templateId: '서식',
  ownerType: '첨부 대상 종류',
  ownerId: '첨부 대상',
  groupId: '옵션 그룹',
  // 첨부·파일
  originalFilename: '파일명',
  contentType: '파일 형식',
  byteSize: '파일 크기(바이트)',
  s3Key: '저장 키',
  pdfS3Key: '서식 PDF 키',
  finalPdfS3Key: '완성 PDF 키',
  uploadedById: '올린 사람',
  pageCount: '쪽수',
  // 영업기회
  humanId: '번호',
  salesRepCode: '영업 담당',
  stage: '단계',
  category: '분류',
  expectedRevenue: '예상 매출',
  expectedRobotCount: '예상 대수',
  expectedCloseAt: '예상 마감일',
  // 인프라 발주
  gasType: '가스 종류',
  installScheduledAt: '설치 예정일',
  installCompletedAt: '설치 완료일',
  customerContactName: '고객 담당자',
  customerContactPhone: '담당자 연락처',
  // 로더·개조
  serial: '시리얼',
  gen: '세대',
  productionKind: '생산 구분',
  productionYm: '생산 월',
  vendor: '공급사',
  configCode: '구성 코드',
  inspectionResult: '검사 결과',
  loaderSerial: '로더 시리얼',
  // 제조 계획
  batchCode: '배치 코드',
  plannedCount: '계획 수량',
  sensorType: '센서 타입',
  estimatedCompletionAt: '완료 예정일',
  // 리포트
  displayId: '접수번호',
  severity: '심각도',
  description: '내용',
  contact: '연락처',
  resolution: '처리 결과',
  // 옵션 값
  sortOrder: '정렬 순서',
  archived: '보관됨',
  // 서명
  documentType: '문서 종류',
  templateVersion: '서식 버전',
  lineageId: '계보 ID',
  // 곁가지(sideEffects)
  s3ObjectDeleted: '파일도 삭제됨',
  dealActivityCount: '함께 지워진 활동 기록',
  dealStageEventCount: '함께 지워진 단계 이력',
  unlinkedEcnMappingCount: '함께 끊긴 변경점 매핑',
  removedHistoricalAllocationCount: '함께 지워진 과거 할당',
  removedAttachmentCount: '함께 지워진 첨부',
  removedStatusEventCount: '함께 지워진 상태 이력',
  removedRobotEntryCount: '함께 지워진 로봇 항목',
  orphanedServiceOrderCount: '고아가 된 서비스오더 수',
  orphanedServiceOrderIds: '고아가 된 서비스오더',
  includeHistory: '이력까지 함께 삭제',
};

/**
 * 자원마다 뜻이 갈리는 칸 — 액션별로 따로 적는다.
 * 같은 'status' 라도 리포트에서는 접수 상태, 로더에서는 로더 상태다.
 */
const DELETE_SNAPSHOT_ACTION_FIELD_LABELS: Record<string, Record<string, string>> = {
  'robot.permanent-delete': { status: '로봇 상태', locationCategory: '위치 분류' },
  'loader.hard-delete': { status: '로더 상태', seq: '일련번호' },
  'loaderRev.hard-delete': {
    code: '개조 코드',
    displayName: '개조 이름',
    seq: '순번',
    active: '사용 중',
  },
  'ecn.hard-delete': { code: '변경점 코드' },
  'report.hard-delete': { status: '접수 상태' },
  'production-plan.hard-delete': { status: '계획 상태' },
  'signature-document.hard-delete': { status: '서명 상태' },
  'signature-template.hard-delete': { name: '서식 이름', version: '버전' },
  'option-value.hard-delete': { value: '값', label: '표시 이름' },
  'deal.hard-delete': { deletedAt: '삭제 표시 시각' },
};

/** 스냅샷 표의 칸 이름 — 액션별 표 → 스냅샷 공통표 → 일반 diff 표 → 원문 키. */
export function formatDeleteSnapshotFieldLabel(field: string, action?: string): string {
  const scoped = action ? DELETE_SNAPSHOT_ACTION_FIELD_LABELS[action]?.[field] : undefined;
  return scoped ?? DELETE_SNAPSHOT_FIELD_LABELS[field] ?? DIFF_FIELD_LABELS[field] ?? field;
}

/** diff 표·문장의 필드명 — 등록된 키는 한국어, 아니면 원문 키 그대로. */
export function formatDiffFieldLabel(field: string, action?: string): string {
  const scoped = action ? ACTION_SCOPED_DIFF_FIELD_LABELS[action]?.[field] : undefined;
  return scoped ?? DIFF_FIELD_LABELS[field] ?? field;
}

// ============================================================================
// ④ diff 값
// ============================================================================

/**
 * 계약 형태가 실리는 **단 하나의** diff 키. customer-master.service
 * projectForAudit 의 키와 1:1 이고 다른 리소스의 diff 에는 이 키가 없다.
 */
const CONTRACT_TYPE_DIFF_FIELD = 'contractType';

/**
 * 금액 칸. **감사 장부에서는 0 원과 미입력을 가른다** — 화면(목록·상세)의 '0=없음'
 * 규칙과 일부러 다르다.
 *
 * 개선 9건 차수2b(26-08-04) — 차수 2a 감사 확정 P3. 종전엔 이 칸이 화면 표기 정본
 * (lib/amount-or-none 의 formatKrwOrNone)을 그대로 불러 **0 과 null 을 둘 다 '–'로
 * 접었다.** 그래서 "지원금 0원 → 미입력" 같은 변경이 감사 표와 문장에 **'– → –'** 로
 * 찍혀 **아무 일도 없었던 것처럼** 보였다.
 *
 * 왜 여기만 예외인가:
 *   • 화면의 '0=없음'은 옳다 — 이 시스템의 금액 컬럼은 NOT NULL 이 많아 '비움'이 0 으로
 *     저장되므로, 그 0 을 금액으로 찍으면 화면이 "0원 계약"이라는 거짓을 말한다.
 *   • 감사 장부는 값을 **보여 주는** 자리가 아니라 "무엇이 어떻게 바뀌었는지"를
 *     **적는** 자리다. 여기서 접으면 기록 자체가 거짓이 되고, 이 장부는 append-only
 *     해시체인이라 **나중에 고칠 수도 없다.**
 *   • 차수 0 의 스키마·DTO·서비스가 이 구분을 일부러 지켰다(지원금·소개비:
 *     null=미입력, 0=명시적 0원). 표시층이 다시 접으면 서버가 애써 지킨 사실이
 *     운영자에게 도달하지 못한다.
 *
 * ⚠ 다음 사람에게 — '일관성'을 이유로 formatKrwOrNone 으로 되돌리지 마라. 화면 쪽
 *   '0=없음'은 그대로 두고 **감사만** 가르는 것이 이 예외의 전부다. 시험이 못박고
 *   있다(audit-log-format.test.ts — "★ 감사 장부는 0원과 미입력을 가른다").
 */
const MONEY_DIFF_FIELDS = new Set(['depositKrw', 'subsidyKrw', 'referralFeeKrw']);

/**
 * 감사용 원화 표기 — **접지 않는다.** 0 은 '₩0', 값 없음은 이 함수에 오기 전에
 * 이미 '–'로 갈라진다(formatDiffValue 머리의 null/'' 분기).
 *
 * 표기 형태는 formatKrwOrNone 과 같은 `₩1,000,000` 이다 — 금액 글자 모양이 감사에서만
 * 달라지면 운영자가 다른 값으로 읽는다. 다른 것은 **0 을 접는가** 하나뿐이다.
 */
function formatKrwForAudit(value: string | number): string {
  const n = Number(value);
  // 숫자가 아니면(서버가 형식을 바꾼 경우) 원문 그대로 — 조용히 금액을 없애지 않는다.
  return Number.isFinite(n) ? `₩${n.toLocaleString('ko-KR')}` : String(value);
}

/**
 * 값이 enum 인 칸의 번역표.
 *
 * ⚠ 계약 형태(contractType)는 **여기 넣지 않는다.** 그 칸은 값별 대수까지 담은
 *   요약 문자열이고, 접어 버리면 파일럿→구독 변경이 '구독 → 구독'이 되어 없던 일이
 *   된다(직전 차수가 세운 규칙 — formatContractTypeSummary 로 따로 번역한다).
 *
 * ⚠ segment·grillSelector* 는 enum 처럼 보이지만 표를 두지 않는다:
 *   - segment 의 표기 정본은 운영자가 늘리는 OptionValue 라벨이다(정적 표를 두면
 *     운영자가 고친 이름과 갈린다).
 *   - grillSelectorType/Install 은 Prisma 상 자유 문자열이고 이미 한국어다
 *     (LPG / 자체 / 발주 전 …).
 *
 * ⚠ `returnReason` 은 **한 키에 두 성질**이 실린다(차수2b 에 표가 붙은 자리):
 *   - Customer.returnReason(고객사 마스터) — 운영자가 직접 적는 자유 문장.
 *   - Deployment.returnReason(회수) — DeploymentReturnReason enum 4값.
 *   그래서 shared 의 enum 정본을 표로 달았다. 자유 문장은 표에 없으니 폴백으로
 *   원문이 그대로 나가고(종전과 동일), 회수 감사 행만 한국어가 된다. 자유 문장이
 *   우연히 'CONTRACT_END' 같은 대문자 enum 키와 똑같을 때만 뜻이 겹치는데, 그 칸에
 *   운영자가 적는 것은 한국어 사유 문장이라 실제로 일어나지 않는다.
 */
/**
 * 로봇 위치 분류 6값의 표기.
 *
 * 4값(AS 센터·공장·연구소·창고)은 **shared 의 회수 장소 정본**을 그대로 쓴다. 남은
 * 두 값은 회수 장소가 아니어서 그 정본에 없으므로 여기서 덧붙인다. 문구는 로봇
 * 목록/상세의 위치 배지(components/StatusPill LOCATION_LABEL)와 같은 말이어야
 * 하고, 그 정합은 audit-log-format.test.ts 의 드리프트 시험이 지킨다(StatusPill 은
 * 라벨을 내보내지 않아 import 로 묶을 수 없다).
 */
export const LOCATION_CATEGORY_LABELS: Record<string, string> = {
  ...ROBOT_RETURN_LOCATION_LABELS,
  CUSTOMER_SITE: '고객 현장',
  DISPOSAL: '폐기',
};

/**
 * 개선 9건 차수2b(26-08-04) — 자산 확인 상태(로봇 일반 편집 diff 의 verificationStatus).
 *
 * 문구 출처는 로봇 목록/상세의 배지와 같은 @bhops/shared 정본(ROBOT_VERIFICATION_LABELS, 2026-10-09)이다 —
 * 운영자가 같은 로봇을 두고 대조하는 화면 · 기준일 마감본 CSV 와 같은 말. **값이 빠지지 않는 것**은 시험이
 * shared enum 전수와 대조해 지킨다.
 */
export const VERIFICATION_STATUS_LABELS: Record<string, string> = { ...ROBOT_VERIFICATION_LABELS };

/**
 * 개선 9건 차수2b(26-08-04) — 배치 종류(배치 diff 의 kind).
 *
 * 2026-09-18 — 문구 출처를 **`@bhops/shared` 정본**으로 옮겼다. 종전엔 여기와
 * 출고 이력 화면과 배치 창이 각자 적고 있어서, 종류를 늘릴 때마다 한 곳이 빠져
 * 감사 장부에 영문 코드가 그대로 떴다(인증·테스트를 더하며 관문이 실제로 잡았다).
 */
export const DEPLOYMENT_KIND_LABELS: Record<string, string> = SHARED_DEPLOYMENT_KIND_LABELS;

/**
 * 개선 9건 차수2b(26-08-04) — 계약 상태(계약 diff 의 status). 문구 출처는 계약 목록·
 * 상세(pages/ContractsPage · pages/contracts/ContractDetailPage STATUS_LABEL — 두
 * 화면이 같은 네 글자를 쓴다).
 */
export const CONTRACT_STATUS_LABELS: Record<string, string> = {
  DRAFT: '초안',
  ACTIVE: '활성',
  EXPIRED: '만료',
  TERMINATED: '해지',
};

/**
 * 개선 9건 차수2b(26-08-04) — 해지 사유(계약 diff 의 terminationReason).
 *
 * 해지 창(ContractTerminateModal REASON_LABEL)의 문구는 괄호에 영문을 달고 있다
 * ('조기 해지 (Early breakoff)') — 값을 **고르는** 자리의 단서라 그 자리에서는 옳지만,
 * 감사 표에는 한국어만 적는다(표의 값 칸은 좁고, 읽는 사람은 이미 그 사유를 고른
 * 사람이 아니다). 그래서 문구를 그대로 베끼지 않고 머리 단어만 가져왔다.
 */
export const CONTRACT_TERMINATION_REASON_LABELS: Record<string, string> = {
  EARLY_BREAKOFF: '조기 해지',
  CHURN_CASCADE: '이탈 연쇄',
  RENEWAL_REPLACED: '재계약 대체',
  CUSTOMER_REQUEST: '고객사 요청',
  OTHER: '기타',
};

const ENUM_VALUE_LABELS: Record<string, Record<string, string>> = {
  // 'status' 는 **세 뜻**이 한 키를 쓴다(고객사 진행 상태 / 로봇 상태 / 계약 상태 —
  // 세 번째는 차수2b 의 계약 diff 가 들여왔다). 세 표의 키는 서로 겹치지 않으므로
  // (소문자 스네이크 vs 대문자 스네이크 두 벌) 합쳐도 안전하다 — 겹치기 시작하면
  // 시험이 깨진다. 어느 뜻인지는 필드**명** 쪽에서
  // ACTION_SCOPED_DIFF_FIELD_LABELS 가 가른다.
  status: { ...CUSTOMER_MASTER_STATUS_LABEL, ...ROBOT_STATUS_LABELS, ...CONTRACT_STATUS_LABELS },
  // 계획서 요구 2a §7ⓐ(26-08-21) — 수동 고정 칸의 값은 고객사 진행 상태 enum 그대로다
  // (자동 복귀는 'retired' 만 풀지만, 고정 자체는 어느 상태로든 걸 수 있다).
  manualStatusOverride: CUSTOMER_MASTER_STATUS_LABEL,
  locationCategory: LOCATION_CATEGORY_LABELS,
  // 같은 건 합치기(2026-11-03) — 남긴 고객사 리포트의 상태.
  reportStatus: SERVICE_ORDER_STATUS_LABELS,
  // ── 차수2b 저장 확대분 ──────────────────────────────────────────────
  robotStatus: ROBOT_STATUS_LABELS,
  robotLocation: LOCATION_CATEGORY_LABELS,
  verificationStatus: VERIFICATION_STATUS_LABELS,
  kind: DEPLOYMENT_KIND_LABELS,
  // 배치 사유는 계약 줄 사유와 같은 enum(DeploymentReason)이고 표기 정본이
  // lib/contract-reason 에 있다 — 두 벌로 늘리지 않는다.
  reason: CONTRACT_REASON_LABEL,
  returnReason: DEPLOYMENT_RETURN_REASON_LABELS,
  terminationReason: CONTRACT_TERMINATION_REASON_LABELS,
  // 구분 수동 정정(plan 2-5) — 접힌 '구분' 토큰. 표기 정본(lib/contract-reason 의
  // 판매/구독)과 같은 단어를 쓰되, 여기 값은 원문 enum 이 아니라 접힌 토큰이라
  // 두 값만 적는다(풀림은 null → '–').
  contractKindOverride: { SALE: '판매', SUBSCRIPTION: '구독' },
};

/**
 * diff 표(그리고 문장)의 값 한 칸.
 *
 * `field` 를 받는 이유 — 값의 뜻이 칸마다 다르다. 필드를 가리지 않고 번역하면
 * 다른 칸의 표기가 조용히 바뀐다(계약 형태 번역이 '진행 상태' 칸을 덮는 사고).
 */
export function formatDiffValue(v: unknown, field?: string): string {
  if (v === null || v === undefined || v === '') return '–';
  if (typeof v === 'boolean') return v ? 'O' : 'X';

  // 금액 — **0 은 '₩0', 미입력은 위에서 이미 '–'.** 접지 않는 이유는
  // MONEY_DIFF_FIELDS 주석 참조(감사 예외). 문자열/숫자 둘 다 온다(서버 직렬화가 갈린다).
  if (field && MONEY_DIFF_FIELDS.has(field) && (typeof v === 'string' || typeof v === 'number')) {
    return formatKrwForAudit(v);
  }

  if (typeof v === 'string') {
    // 계약 형태 — 서버 요약(원문 값·대수)을 한국어로. 요약 형태가 아니면 null 이
    // 돌아오므로 아래 폴백이 살아 있다(옛 기록의 '판매 1 · 렌탈 2' 포함).
    if (field === CONTRACT_TYPE_DIFF_FIELD) {
      const ko = formatContractTypeSummary(v);
      if (ko) return ko;
    }
    // 값이 enum 인 칸 — 정본 라벨로. 모르는 값은 원문 폴백(틀린 한국어로 뭉개지
    // 않는다).
    if (field) {
      const enumLabel = ENUM_VALUE_LABELS[field]?.[v];
      if (enumLabel) return enumLabel;
    }
    // ISO 일시는 날짜만 — KST 로 환산해 자른다(문자열을 그대로 자르면 한국 새벽에
    // 바뀐 값이 전날로 보인다).
    const iso = /^\d{4}-\d{2}-\d{2}T/.exec(v);
    return iso ? (kstYmd(v) ?? v) : v;
  }
  return String(v);
}

/**
 * `computeFieldDiff` 가 만드는 `{field:{from,to}}` 형태인지 판별해 그 타입으로 좁힌다.
 * 그 밖의 형태(과거 행·요약 payload)는 호출부가 raw JSON 으로 보여 준다.
 */
export function asFieldDiff(diff: unknown): Record<string, { from: unknown; to: unknown }> | null {
  if (!diff || typeof diff !== 'object' || Array.isArray(diff)) return null;
  const values = Object.values(diff as Record<string, unknown>);
  // 빈 객체({})는 "호출됐고 바뀐 값 없음"을 뜻하는 정당한 diff 다 — 형태로 인정한다.
  if (values.length === 0) return diff as Record<string, { from: unknown; to: unknown }>;
  const ok = values.every(
    (v) =>
      !!v &&
      typeof v === 'object' &&
      !Array.isArray(v) &&
      'from' in (v as object) &&
      'to' in (v as object),
  );
  return ok ? (diff as Record<string, { from: unknown; to: unknown }>) : null;
}
